import axios, { AxiosResponse } from 'axios';
import { toJS } from 'mobx';
import { getAPIErrorReason, getCookieValue, isDev } from '../helpers/common';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import { WORKSPACE_TEAM_TYPE } from '../global/constants';
import {
  WorkspaceInputDetailsInterface,
  WorkspaceInterface,
  updateWorkspacePayloadInterface
} from '../interfaces/WorkspaceInterface';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { setAnalyticUserAndCompany, trackAnalyticActivity, workspaceSetAnalytics } from './analyticsService';
import { captureException } from './logService';
import { setWorkspaceIdInAxios } from '../helpers/authTokenHelpers';
import { WORKSPACE_ANALYTICS } from '../global/analyticsConstants';
import { rootStore } from '../mobx/rootStore';
import { RawWorkspace } from '../mobx/interfaces/workspace';

declare global {
  interface Window {
    posthog?: {
      group: (type: string, id: string, properties: Record<string, unknown>) => void;
    };
  }
}

// Shared across callers so concurrent fetches (e.g. appInit + page mount) hit the API once.
// Only the request is shared; each caller still applies its own store updates.
const inFlightWorkspaceRequests = new Map<string, Promise<AxiosResponse>>();

// force: skip joining an in-flight request, e.g. right after a mutation so the result includes it
const dedupeWorkspaceRequest = (key: string, params: Record<string, string | undefined>, force = false) => {
  const pending = inFlightWorkspaceRequests.get(key);
  if (pending && !force) return pending;
  const request = axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/getWorkspaceList`, { params }).finally(() => {
    if (inFlightWorkspaceRequests.get(key) === request) inFlightWorkspaceRequests.delete(key);
  });
  inFlightWorkspaceRequests.set(key, request);
  return request;
};

// A refresh without lastSeen must not wipe values already loaded (e.g. by the Teams page) for the same workspace
const keepKnownLastSeen = (workspace: RawWorkspace): RawWorkspace => {
  const current = rootStore.workspaceStore.currentWorkspace;
  if (!current || current.id !== workspace.id) return workspace;
  const lastSeenById = new Map(current.users.map((user) => [user.id, user.lastSeen]));
  return {
    ...workspace,
    users: workspace.users?.map((user) => ({ ...user, lastSeen: user.lastSeen || lastSeenById.get(user.id) || '' }))
  };
};

// The list (id, name, logo) rarely changes, so app reloads reuse a stored copy for a while instead of refetching.
// Stored in user preferences, so logout clears it. Create/delete workspace and login refetch it.
const WORKSPACE_LIST_CACHE_TTL_MS = 30 * 60 * 1000;

export const clearWorkspaceListCache = () => UserPreferenceSingleton.getInstance().setWorkspaceListCache(null);

const loadWorkspaceListFromCache = () => {
  const cache = UserPreferenceSingleton.getInstance().getWorkspaceListCache();
  const userId = UserPreferenceSingleton.getInstance().getCurrentUser()?.id;
  const isValid =
    cache &&
    cache.userId === userId &&
    cache.list?.length > 0 &&
    Date.now() - cache.fetchedAt < WORKSPACE_LIST_CACHE_TTL_MS;
  if (!isValid) return false;
  rootStore.workspaceStore.setWorkspaceList(cache.list);
  return true;
};

/**
 * @desc Workspace - Get workspace list
 * @param options.useCache - reuse the stored list if it is recent instead of calling the API
 */
export const getWorkspaceList = async ({ useCache = false }: { useCache?: boolean } = {}) => {
  if (useCache && loadWorkspaceListFromCache()) return true;
  try {
    rootStore.workspaceStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const params = {
      user_id: userDetails?.id
    };
    const response = await dedupeWorkspaceRequest(`list:${userDetails?.id}`, params);
    const { data } = response;
    if (data.length > 0) {
      rootStore.workspaceStore.setWorkspaceList(data);
      UserPreferenceSingleton.getInstance().setWorkspaceListCache({
        userId: userDetails?.id,
        fetchedAt: Date.now(),
        list: data
      });
      const workspace = UserPreferenceSingleton.getInstance().getWorkspace();
      if (!workspace) {
        const selectedWorkspace = data[0];
        UserPreferenceSingleton.getInstance().setWorkspace(selectedWorkspace);
        setWorkspaceIdInAxios(selectedWorkspace?.id);
        workspaceSetAnalytics(selectedWorkspace?.id);
      }
    } else if (data && data?.length === 0) {
      clearWorkspaceListCache();
      UserPreferenceSingleton.getInstance().setWorkspace(null);
      rootStore.workspaceStore.setCurrentWorkspace(null);
      rootStore.workspaceStore.setWorkspaceList([]);
    }
    return true;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get workspace list please try again';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

/**
 * @desc Workspace - Get workspace details
 * @param options.force - skip joining an in-flight request (use right after a mutation)
 * @param options.includeLastSeen - load each member's lastSeen; costs the backend a full UserDevices scan
 */
export const getWorkspaceDetails = async (
  workspace_id: string,
  { force = false, includeLastSeen = false }: { force?: boolean; includeLastSeen?: boolean } = {}
) => {
  try {
    if (!workspace_id) return false;
    rootStore.workspaceStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const params = {
      user_id: userDetails?.id,
      workspace_id: workspace_id,
      include_last_seen: includeLastSeen ? undefined : 'false'
    };
    const response = await dedupeWorkspaceRequest(
      `details:${userDetails?.id}:${workspace_id}:${includeLastSeen}`,
      params,
      force
    );
    const data = response.data && !includeLastSeen ? keepKnownLastSeen(response.data) : response.data;
    // A workspace the (possibly cached) list doesn't know about, or one the user no longer has access to,
    // means the list is outdated
    const { workspaceList } = rootStore.workspaceStore;
    if (!data?.id) clearWorkspaceListCache();
    else if (workspaceList && !workspaceList.some((item) => item.id === data.id)) {
      clearWorkspaceListCache();
      getWorkspaceList();
    }
    // The API returns [] when the user has no access to the workspace
    if (data?.id) {
      setWorkspaceIdInAxios(data?.id);
      workspaceSetAnalytics(data?.id);
      UserPreferenceSingleton.getInstance().setWorkspace(data);
      rootStore.workspaceStore.setCurrentWorkspace(data);
      return data;
    } else {
      UserPreferenceSingleton.getInstance().setWorkspace(null);
      rootStore.workspaceStore.setCurrentWorkspace(null);
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get workspace details please try again';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

/**
 * @desc Workspace - Apply known changes to the current workspace without refetching
 */
export const updateCurrentWorkspaceLocally = (
  changes: Partial<Pick<WorkspaceInterface, 'name' | 'email' | 'phone' | 'logoUrl' | 'apps'>>
) => {
  rootStore.workspaceStore.updateCurrentWorkspace(changes);
  const cachedWorkspace = UserPreferenceSingleton.getInstance().getWorkspace();
  if (cachedWorkspace) UserPreferenceSingleton.getInstance().setWorkspace({ ...cachedWorkspace, ...changes });
  const listCache = UserPreferenceSingleton.getInstance().getWorkspaceListCache();
  const { workspaceList } = rootStore.workspaceStore;
  if (listCache && workspaceList) {
    UserPreferenceSingleton.getInstance().setWorkspaceListCache({ ...listCache, list: toJS(workspaceList) });
  }
};

/**
 * @desc Workspace - Create new workspace
 */
export const createWorkspace = async (payloadData: WorkspaceInputDetailsInterface) => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

    // Extract cookie values
    const utmSource = getCookieValue('utm_source');
    const utmMedium = getCookieValue('utm_medium');
    const utmCampaign = getCookieValue('utm_campaign');
    const utmTerm = getCookieValue('utm_term');
    const utmContent = getCookieValue('utm_content');
    const referrer_url = getCookieValue('referrer_url');
    const lpPath = getCookieValue('lp_path');

    const payload = {
      ...payloadData,
      userId: userDetails?.id,
      isDev: isDev(),
      referrer_url: referrer_url ? referrer_url : undefined,
      lpPath: lpPath ? lpPath : undefined
    };
    if (utmSource || utmMedium || utmCampaign || utmTerm || utmContent) {
      payload.utmData = {
        utmSource,
        utmMedium,
        utmCampaign,
        utmTerm,
        utmContent
      };
    }
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/postWorkspace`, payload);
    const { data } = response.data;
    if (data) {
      const eventProperties = {
        workspace_name: data?.name,
        workspace_type: WORKSPACE_TEAM_TYPE[data?.teamType],
        industry: data?.industry
      };
      trackAnalyticActivity(WORKSPACE_ANALYTICS.CREATED, eventProperties);
      const intercomPayload = {
        company: {
          name: data?.name,
          id: data?.id
        }
      };
      const posthog = window.posthog;
      if (posthog) {
        posthog.group('Workspace', intercomPayload.company?.id, {
          name: intercomPayload.company?.name
        });
      }
      setWorkspaceIdInAxios(data?.id);
      workspaceSetAnalytics(data?.id);
      UserPreferenceSingleton.getInstance().setWorkspace(data);
      rootStore.workspaceStore.setCurrentWorkspace(data);
      clearWorkspaceListCache();
      getWorkspaceList();
      setAnalyticUserAndCompany(userDetails, data);
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Create Workspace please try again';
    captureException(error, 'onboarding');
    dispatchWorkspaceError(error);
    return undefined;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

/**
 * @desc Workspace - Check step one form validation
 * @param {*}
 */
export const checkStepOneValidation = async (payload: WorkspaceInputDetailsInterface) => {
  try {
    if (!payload || !payload.name || payload?.name.trim() === '') {
      dispatchWorkspaceError('Name is required');
      return false;
    } else if (!payload.teamType) {
      dispatchWorkspaceError('Team type is required');
      return false;
    } else if (!payload.industry || payload?.industry?.trim() === '') {
      dispatchWorkspaceError('Industry is required');
      return false;
    }
    return true;
  } catch (e) {
    captureException(e);
    return false;
  }
};

/**
 * @desc Workspace - Update workspace details
 */
export const onUpdateWorkspaceDetails = async (workspace_id: string, payloadData: updateWorkspacePayloadInterface) => {
  try {
    if (!workspace_id) return false;
    rootStore.workspaceStore.setLoading(true);
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    const payload = {
      ...payloadData,
      companyId: workspace_id,
      userId: userDetails?.id
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/putWorkspaceDetails`, payload);
    if (response) {
      if (response.data.message) {
        rootStore.alertNotificationStore.setSuccessMessage(response.data.message);
        return true;
      } else if (response.data.error) {
        rootStore.alertNotificationStore.setErrorMessage(response.data.error);
        return false;
      }
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update workspace details please try again';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

/**
 * @desc Workspace - Get usage details
 */
export const getWorkspaceUsageDetails = async () => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/getUsageStorage`);
    if (response) {
      if (response.data) {
        return response.data;
      }
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get workspace usage details please try again';
    captureException(error);
    dispatchWorkspaceError(error);
    return undefined;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

/**
 * @desc Workspace - Send Delete Request workspace
 */
export const sendDeleteRequest = async (isResend: boolean) => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/company/deleteRequest`);
    if (response) {
      rootStore.alertNotificationStore.setSuccessMessage(
        isResend ? 'Verification code resend successfully' : 'Delete request send'
      );
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to send delete request for workspace. Please try again.';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

/**
 * @desc Workspace - Delete workspace
 */
export const deleteWorkspace = async (companyId: string, verificationCode: string) => {
  try {
    if (!companyId || !verificationCode) return false;
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/company/${companyId}/delete/${verificationCode}`);
    if (response) {
      clearWorkspaceListCache();
      rootStore.alertNotificationStore.setSuccessMessage('Workspace deleted successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete workspace, please try again.';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

export const activateAgent = async (agentId: string) => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/agents/enableAgent`, { agentId });
    if (response) {
      rootStore.alertNotificationStore.setSuccessMessage('Workspace activated successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to activate agent, please try again.';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

export const deactivateAgent = async (agentId: string) => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/agents/disableAgent`, { agentId });
    if (response) {
      rootStore.alertNotificationStore.setSuccessMessage('Agent deactivated successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to deactivate agent, please try again.';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

export const addAgentToProject = async (agentId: string, projectId: string) => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/agents/addAgentToProject/${projectId}`, {
      agentId
    });
    if (response) {
      rootStore.alertNotificationStore.setSuccessMessage('Agent added to project successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add agent to project, please try again.';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

export const removeAgentFromProject = async (agentId: string, projectId: string) => {
  try {
    rootStore.workspaceStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/agents/removeAgentFromProject/${projectId}`, {
      agentId
    });
    if (response) {
      rootStore.alertNotificationStore.setSuccessMessage('Agent removed from project successfully');
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove agent from project, please try again.';
    captureException(error);
    dispatchWorkspaceError(error);
    return false;
  } finally {
    rootStore.workspaceStore.setLoading(false);
  }
};

export function dispatchWorkspaceError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}
